import { Component, OnInit, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TreeNode } from 'primeng/api';
import { CatalogService } from '../../../services/catalog.service';
import { ProductCategoriesService } from '../../../services/product-categories.service';
import { ProductTagsService } from '../../../services/product-tags.service';
import { ProductService } from '../../../services/product.service';
import { FascicleService } from '../../../services/fascicle.service';
import { fascicleAutoFormFields } from '../../../schemas/fascicle-auto-form.schema';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserService } from '../../../../user/services/user.service';
import { MessageService } from 'primeng/api';
import { PageTemplateService } from 'app/modules/catalog-cloud/services/page-template.service';
import { firstValueFrom } from 'rxjs';
import { PageService } from 'app/modules/catalog-cloud/services/page.service';
import { Fascicle, Product, Page, PageTemplate, CellTemplate, Category } from '@nousmedis/cc-types';
import { TranslateService } from '@ngx-translate/core';

interface SortRule {
  field: string;
  direction: 'asc' | 'desc';
}

interface GenerationRules {
  fillEmptyCells: boolean;
  maxProductsOverride: number | null;
  skipUnavailable: boolean;
  duplicateStrategy: 'allow' | 'skip';
}

interface FascicleConfig {
  id: string;
  name: string;
  defaultTemplate: string;
  categories: string[];
  tags: string[];
  sortRules: SortRule[];
  generationRules: GenerationRules;
  selectedCategoryNodes: TreeNode[];
  totalProducts: number;
  searchingProducts: boolean;
  status: 'pending' | 'generating' | 'done' | 'error';
  generatedFascicleId?: string;
  errorMessage?: string;
}

@Component({
  selector: 'app-fascicle-auto-form',
  templateUrl: './fascicle-auto-form.component.html',
  styleUrl: './fascicle-auto-form.component.scss',
})
export class FascicleAutoFormComponent implements OnInit {
  form = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];

  loadedForm = false;
  spinnerSave = false;
  searchingProducts = false;
  totalProducts = 0;
  totalCatalogProducts = 0;

  categoryTreeNodes: TreeNode[] = [];
  selectedCategoryNodes: TreeNode[] = [];
  tagOptions: { label: string; value: string }[] = [];

  fascicleConfigs: FascicleConfig[] = [];
  overallProgress = { current: 0, total: 0 };
  productFieldOptions: { label: string; value: string }[] = [];
  templateOptions: { label: string; value: string }[] = [];

  readonly directionOptions = [
    { label: 'A→Z', value: 'asc' },
    { label: 'Z→A', value: 'desc' },
  ];

  duplicateStrategyOptions: { label: string; value: string }[] = [];

  private idCatalog = '';
  private templates: PageTemplate[] = [];

  constructor(
    private catalogService: CatalogService,
    private fascicleService: FascicleService,
    private categoriesService: ProductCategoriesService,
    private tagsService: ProductTagsService,
    private pageTemplateService: PageTemplateService,
    private productService: ProductService,
    private userService: UserService,
    private messageService: MessageService,
    private pageService: PageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() private dialogConfig?: DynamicDialogConfig,
    @Optional() private dialogRef?: DynamicDialogRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.idCatalog = this.dialogConfig?.data?.idCatalog ?? this.route.snapshot.paramMap.get('idCatalog') ?? '';

    const [templates, tags, categories, products] = await Promise.all([
      firstValueFrom(this.pageTemplateService.getTemplates()),
      firstValueFrom(this.tagsService.getByCatalog(this.idCatalog)),
      firstValueFrom(this.categoriesService.getByCatalog(this.idCatalog)),
      firstValueFrom(this.productService.getProducts(this.idCatalog, [])),
    ]);

    this.templates = templates;

    this.categoryTreeNodes = this.buildCategoryTree(categories);

    this.tagOptions = tags.map((t: string) => ({ label: t, value: t }));

    this.templateOptions = templates.map((t: PageTemplate) => ({
      label: t.name,
      value: t.id,
    }));

    this.productFieldOptions = this.buildSortableFields(products);
    this.totalCatalogProducts = products.length;

    this.duplicateStrategyOptions = [
      { label: this.translate.instant('FASCICLE.DUPLICATE_ALLOW'), value: 'allow' },
      { label: this.translate.instant('FASCICLE.DUPLICATE_SKIP'), value: 'skip' },
    ];

    this.fields = fascicleAutoFormFields(this.translate, this.templateOptions);

    const userId = this.userService.getCurrentUser().id;
    const fascicle = this.fascicleService.initialize(userId);
    this.model = {
      ...fascicle,
      idCatalog: this.idCatalog,
      categories: [],
      tags: [],
      enableOrder: false,
      orderField: '',
    };

    this.loadedForm = true;
    // Inicializar con un fascículo por defecto
    this.addFascicleConfig();
  }

  onCategoriesChange(config: FascicleConfig): void {
    config.categories = config.selectedCategoryNodes.map(n => n.key!);
    this.countFilteredProducts(config);
  }

  private buildCategoryTree(categories: Category[]): TreeNode[] {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    for (const c of categories) {
      map.set(c.externalId, { key: c.id, label: (c as any).composedName || c.name, children: [] });
    }

    for (const c of categories) {
      const node = map.get(c.externalId)!;
      if (c.idParent && map.has(c.idParent)) {
        map.get(c.idParent)!.children!.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }

  onTagsChange(config: FascicleConfig): void {
    this.countFilteredProducts(config);
  }

  countFilteredProducts(config: FascicleConfig): void {
    config.searchingProducts = true;
    this.productService
      .countProducts(this.idCatalog, config.categories, config.tags)
      .subscribe((data) => {
        config.totalProducts = Number(data) || 0;
        config.searchingProducts = false;
      });
  }

  addFascicleConfig(): void {
    const config: FascicleConfig = {
      id: Date.now().toString(),
      name: '',
      defaultTemplate: this.templateOptions.length ? this.templateOptions[0].value : '',
      categories: [],
      tags: [],
      sortRules: [],
      generationRules: {
        fillEmptyCells: true,
        maxProductsOverride: null,
        skipUnavailable: false,
        duplicateStrategy: 'allow',
      },
      selectedCategoryNodes: [],
      totalProducts: 0,
      searchingProducts: false,
      status: 'pending',
    };
    this.fascicleConfigs.push(config);
    this.countFilteredProducts(config);
  }

  removeFascicleConfig(index: number): void {
    if (this.fascicleConfigs.length > 1) {
      this.fascicleConfigs.splice(index, 1);
    }
  }

  addSortRule(config: FascicleConfig): void {
    config.sortRules.push({ field: '', direction: 'asc' });
  }

  removeSortRule(config: FascicleConfig, index: number): void {
    config.sortRules.splice(index, 1);
  }

  moveSortRule(config: FascicleConfig, index: number, delta: -1 | 1): void {
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= config.sortRules.length) return;
    [config.sortRules[index], config.sortRules[newIndex]] =
      [config.sortRules[newIndex], config.sortRules[index]];
  }

  save(): void {
    this.spinnerSave = true;
    this.form.disable();
    this.overallProgress = { current: 0, total: this.fascicleConfigs.length };
    const assignedSkus = new Set<string>();

    const runSequential = async () => {
      for (const config of this.fascicleConfigs) {
        config.status = 'generating';
        try {
          const id = await this.generateFromConfig(config, assignedSkus);
          config.status = 'done';
          config.generatedFascicleId = id ?? undefined;
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('FASCICLE.NAME'),
            detail: config.name + ' ' + this.translate.instant('FASCICLE.GENERATED'),
            key: 'root-msg',
          });
        } catch (err) {
          config.status = 'error';
          config.errorMessage = String(err);
          this.messageService.add({
            severity: 'danger',
            summary: 'Error',
            detail: String(err),
            key: 'root-msg',
          });
        }
        this.overallProgress.current++;
      }

      this.spinnerSave = false;
      this.form.enable();

      const allDone = this.fascicleConfigs.every(c => c.status === 'done');
      if (allDone) {
        if (this.dialogRef) {
          this.dialogRef.close();
        } else if (this.fascicleConfigs.length === 1) {
          this.router.navigate(['/catalog', this.idCatalog, 'fascicle', this.fascicleConfigs[0].generatedFascicleId]);
        } else {
          this.router.navigate(['/catalog', this.idCatalog, 'fascicles']);
        }
      }
    };

    runSequential().catch(err => {
      this.spinnerSave = false;
      this.form.enable();
      this.messageService.add({ severity: 'danger', summary: 'Error', detail: String(err), key: 'root-msg' });
    });
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogConfig!.data.cancel = true;
      this.dialogRef.close();
    } else {
      this.router.navigate(['/catalog', this.idCatalog, 'fascicles']);
    }
  }

  private async generateFromConfig(config: FascicleConfig, assignedSkus: Set<string>): Promise<string | null> {
    const templateId = config.defaultTemplate;
    const template = this.templates.find((t) => t.id === templateId);

    if (!template) {
      return Promise.reject('No se ha encontrado la plantilla');
    }

    const visibleCells = template.cells.filter((cell: CellTemplate) => cell.visible);
    const rules = config.generationRules;
    const productsPerPage = rules.maxProductsOverride ?? visibleCells.length;
    const userId = this.userService.getCurrentUser().id;

    const fascicle: Fascicle = {
      ...this.fascicleService.initialize(userId),
      name: config.name,
      categories: config.categories,
      tags: config.tags,
      idCatalog: this.idCatalog,
      defaultTemplate: template.id as any,
    };

    return new Promise<string | null>((resolve, reject) => {
      this.catalogService.getProductTable(this.idCatalog).then((productTable: string) => {
        this.pageService.setPageTemplates([template]);
        firstValueFrom(this.productService.getProducts(this.idCatalog, [])).then((products: Product[]) => {
          let filteredProducts = this.filteredProducts(products, config.categories, config.tags);

          // Aplicar skipUnavailable
          if (rules.skipUnavailable) {
            filteredProducts = filteredProducts.filter(p => p.img && p.img.length > 0);
          }

          // Aplicar duplicateStrategy
          if (rules.duplicateStrategy === 'skip') {
            filteredProducts = filteredProducts.filter(p => !assignedSkus.has(p.sku));
          }

          // Ordenar con reglas multi-campo
          if (config.sortRules.length > 0) {
            this.sortProducts(filteredProducts, config.sortRules);
          }

          const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
          fascicle.pages = this.generatePages(
            totalPages, userId, filteredProducts, visibleCells.length, productTable, rules
          );

          // Registrar SKUs usados para el seguimiento cross-config
          filteredProducts.forEach(p => assignedSkus.add(p.sku));

          firstValueFrom(this.fascicleService.save(fascicle)).then((data: any) => {
            resolve(data?.newFascicleId ?? data?.id ?? null);
          }).catch(reject);
        }).catch(reject);
      }).catch(reject);
    });
  }

  private sortProducts(products: Product[], rules: SortRule[]): void {
    if (!rules.length) return;
    products.sort((a, b) => {
      for (const rule of rules) {
        const valA = this.getFieldValue(a, rule.field);
        const valB = this.getFieldValue(b, rule.field);
        const cmp = String(valA ?? '').localeCompare(String(valB ?? ''), undefined, { numeric: true });
        if (cmp !== 0) return rule.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }

  private getFieldValue(product: Product, field: string): unknown {
    if (field.startsWith('extraFields.')) {
      return product.extraFields?.[field.replace('extraFields.', '')] ?? '';
    }
    return (product as unknown as Record<string, unknown>)[field] ?? '';
  }

  private generatePages(
    totalPages: number,
    userId: any,
    products: Product[],
    cells: number,
    productTable: string,
    rules: GenerationRules
  ): Page[] {
    const paginas: Page[] = [];
    let productIndex = 0;
    for (let i = 0; i < totalPages; i++) {
      // Si fillEmptyCells es false y ya no hay productos, parar
      if (!rules.fillEmptyCells && productIndex >= products.length) break;
      const page = this.pageService.getNewPage(this.idCatalog);
      page.createdBy = userId;
      productIndex = this.generateCells(products, cells, productTable, userId, page, productIndex, rules);
      paginas.push(page);
    }
    return paginas;
  }

  private generateCells(
    products: Product[],
    cells: number,
    productTable: string,
    userId: any,
    page: Page,
    productIndex: number,
    rules: GenerationRules
  ): number {
    for (let j = 0; j < cells; j++) {
      if (productIndex < products.length) {
        if (page.cells[j].visible) {
          page.cells[j].product = products[productIndex];
          productIndex++;
        }
      }
      page.cells[j].createdBy = userId;
      page.cells[j].productTable = productTable;
    }
    return productIndex;
  }

  private filteredProducts(products: Product[], selectedCategories: string[], selectedTags: string[]): Product[] {
    return products.filter((product) => {
      const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(product.idCategory);
      const tagMatch = selectedTags.length === 0 || selectedTags.some((tag) => product.tags.includes(tag));
      return categoryMatch && tagMatch;
    });
  }

  private buildSortableFields(products: Product[]): { label: string; value: string }[] {
    const fields: { label: string; value: string }[] = [
      { label: 'Nombre', value: 'name' },
      { label: 'SKU', value: 'sku' },
      { label: 'Descripción', value: 'description' },
      { label: 'Familia', value: 'family' },
    ];

    const extraKeys = new Set<string>();
    products.slice(0, 10).forEach(p => {
      if (p.extraFields && typeof p.extraFields === 'object') {
        Object.entries(p.extraFields).forEach(([k, v]) => {
          if (v !== null && !Array.isArray(v) && typeof v !== 'object') {
            extraKeys.add(k);
          }
        });
      }
    });
    extraKeys.forEach(k =>
      fields.push({ label: 'Extra: ' + this.capitalizeLetter(k), value: 'extraFields.' + k })
    );
    return fields;
  }

  trackByConfigId(_index: number, config: FascicleConfig): string {
    return config.id;
  }

  isFormValid(): boolean {
    return this.fascicleConfigs.length > 0 &&
      this.fascicleConfigs.every(c => c.name.trim().length > 0 && c.defaultTemplate.length > 0);
  }

  private capitalizeLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}
